import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { Camera } from "lucide-react";
import { api } from "../../services/api";
import { UserModel } from "../../types/user";
import { mergeSessionUser } from "../../utils/session-user";
import { useTranslation } from "react-i18next";

interface ProfileEditModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    me: UserModel;
    currentAvatarSrc: string | null;
    initials: string;
    onUpdated: (user: UserModel) => void;
}

export const ProfileEditModal = ({
    open,
    onOpenChange,
    me,
    currentAvatarSrc,
    initials,
    onUpdated,
}: ProfileEditModalProps) => {
    const { t } = useTranslation("sidebar");
    const inputRef = useRef<HTMLInputElement>(null);
    const [name, setName] = useState(me?.name ?? "");
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(me?.name ?? "");
            setFile(null);
            setPreview(null);
            if (inputRef.current) inputRef.current.value = "";
        }
    }, [open, me?.name]);

    const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (!selected) return;
        setFile(selected);
        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result as string);
        reader.readAsDataURL(selected);
    };

    const trimmedName = name.trim();
    const nameChanged = !!trimmedName && trimmedName !== (me?.name ?? "");
    const hasChanges = nameChanged || !!file;

    const handleSave = async () => {
        if (!me?.id || !hasChanges) return;
        setSaving(true);
        try {
            // `avatar` is a plain string column (the API only ever stores URLs / base64 strings,
            // never a multipart file), so send name + avatar together as a JSON body.
            const payload: Record<string, string> = {};
            if (nameChanged) payload.name = trimmedName;
            if (file && preview) payload.avatar = preview;

            if (Object.keys(payload).length > 0) {
                await api.put(`/user/${me.id}`, payload);
            }

            // Base keeps runtime fields not in UserModel (e.g. accessToken used by the api interceptor).
            let updated: UserModel = { ...me, ...payload };

            // Re-read the authoritative user so the dropdown matches the backend (and other screens).
            try {
                const { data } = await api.get("/user/me");
                const fresh = data?.data ?? data;
                if (fresh?.id) updated = mergeSessionUser(updated, fresh);
            } catch {
                // keep optimistic values if the refetch fails
            }

            onUpdated(updated);
            onOpenChange(false);
        } catch {
            // api interceptor already surfaces the error toast
        } finally {
            setSaving(false);
        }
    };

    const displayed = preview || currentAvatarSrc || undefined;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>{t("profileModal.title")}</DialogTitle>
                    <DialogDescription>{t("profileModal.description")}</DialogDescription>
                </DialogHeader>

                <div className="flex flex-col items-center gap-3 py-2">
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="group relative rounded-full bg-gradient-to-br from-[#9b87f5] via-[#7c3aed] to-[#4f46e5] p-[2px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        aria-label={t("profileModal.change")}
                    >
                        <Avatar className="h-24 w-24 ring-2 ring-card">
                            {displayed ? <AvatarImage src={displayed} alt={me?.name} className="object-cover" /> : null}
                            <AvatarFallback className="bg-primary text-2xl font-semibold text-primary-foreground">
                                {initials}
                            </AvatarFallback>
                        </Avatar>
                        <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 opacity-0 transition-opacity group-hover:opacity-100">
                            <Camera size={22} className="text-white" />
                        </span>
                    </button>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        onChange={handleSelect}
                        title={t("profileModal.avatarInputLabel", "Carregar foto de perfil")}
                        aria-label={t("profileModal.avatarInputLabel", "Carregar foto de perfil")}
                    />
                    <span className="text-xs text-muted-foreground">{t("profileModal.hint")}</span>
                </div>

                <div className="space-y-2">
                    <label htmlFor="profile-name" className="text-sm font-medium text-card-foreground">
                        {t("profileModal.nameLabel")}
                    </label>
                    <Input
                        id="profile-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("profileModal.namePlaceholder")}
                    />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        {t("profileModal.cancel")}
                    </Button>
                    <Button onClick={handleSave} disabled={!hasChanges || saving}>
                        {saving ? t("profileModal.saving") : t("profileModal.save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
